import { Link, useParams } from "react-router-dom";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { BatteryBadge, SignalBadge } from "@/components/parcelio/device-health";
import { LiveMap, markStops, type LiveMapHandle } from "@/components/parcelio/live-map";
import { ReplayMap, interpolatePosition, type ReplayMapHandle } from "@/components/parcelio/replay-map";
import { LocationSearchBox } from "@/components/parcelio/location-search-box";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { vehiclesApi, settingsApi, type Vehicle, type VehicleType, type GpsPing } from "@/lib/api";
import { useAuth } from "@/stores/auth";
import { ArrowLeft, Gauge, Satellite, Clock, Play, Pause, RotateCcw, History, BatteryMedium as BatteryIcon, SignalMedium as SignalIcon, ChevronDown, MapPinned, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";

const POLL_MS = Number(import.meta.env.VITE_GPS_REFRESH_MS) || 30000;

// Playback speed = simulated minutes of vehicle travel per real second of
// animation. Pings land ~10-60s apart depending on tracker config, so these
// aren't literal "ping every Xs" rates — interpolatePosition() moves the
// marker continuously between whatever pings bracket the current simulated
// time, so the multiplier stays meaningful regardless of ping density.
// 10x (~6s of travel/sec) is a close watch; 600x (10 real min -> 1 playback
// sec, matching a quick "did it actually move today" scrub) is the fast end.
const SPEED_OPTIONS = [
  { value: 10, label: "10x — close watch" },
  { value: 60, label: "60x — 1 min/sec" },
  { value: 300, label: "300x — 5 min/sec" },
  { value: 600, label: "600x — 10 min/sec" },
  { value: 1800, label: "1800x — 30 min/sec" },
];
const DEFAULT_SPEED = 60;

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// Live view window: 6am local time (operator's shift start) through now.
// Before 6am there's no "today's shift" yet, so it falls back to showing
// since 6am *yesterday* instead of an empty/tiny window — a dispatcher
// checking at 4am still sees the overnight-to-now trail. Anything older than
// that belongs in Replay, not the live view.
function shiftWindowStart(): Date {
  const now = new Date();
  const sixAm = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 6, 0, 0, 0);
  if (now < sixAm) sixAm.setDate(sixAm.getDate() - 1);
  return sixAm;
}

export default function VehicleTracking() {
  const { vehicleId } = useParams();
  const token = useAuth((s) => s.session?.accessToken);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [types, setTypes] = useState<VehicleType[]>([]);
  const [history, setHistory] = useState<GpsPing[]>([]);
  const [loading, setLoading] = useState(true);
  const prevPingRef = useRef<string | null>(null);
  const mapRef = useRef<LiveMapHandle>(null);
  const replayMapRef = useRef<ReplayMapHandle>(null);

  // Pings received table pagination — client-side, the list is at most 24h
  // of pings (already fetched in full for the map trail).
  const [pingPage, setPingPage] = useState(0);
  const [pingPageSize, setPingPageSize] = useState(10);

  // Live track / Replay are mutually exclusive — one open at a time, live
  // expanded by default.
  const [openSection, setOpenSection] = useState<"live" | "replay">("live");

  // Location search — admin-gated (Settings → App settings) since every
  // query spends geocode provider credit. Fetched once here, shared by both
  // the live and replay search boxes.
  const [searchEnabled, setSearchEnabled] = useState(true);
  useEffect(() => {
    settingsApi
      .get(token)
      .then((s) => setSearchEnabled(s.location_search_enabled !== false))
      .catch(() => {
        // best-effort — leave search enabled if settings fail to load
      });
  }, [token]);

  // Replay state — independent of the live-tracking poll above.
  const [replayFrom, setReplayFrom] = useState(todayStr());
  const [replayTo, setReplayTo] = useState(todayStr());
  const [replayHistory, setReplayHistory] = useState<GpsPing[] | null>(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [speed, setSpeed] = useState(DEFAULT_SPEED);
  const [playing, setPlaying] = useState(false);
  const [cursorMs, setCursorMs] = useState(0);
  const rafRef = useRef<number | null>(null);
  const lastTickRef = useRef<number | null>(null);

  const replayStartMs = replayHistory && replayHistory.length > 0 ? new Date(replayHistory[0].recorded_at).getTime() : 0;
  const replayEndMs =
    replayHistory && replayHistory.length > 0 ? new Date(replayHistory[replayHistory.length - 1].recorded_at).getTime() : 0;

  const runReplay = async () => {
    if (!vehicleId) return;
    if (!replayFrom || !replayTo) return toast.error("Pick a start and end date");
    if (new Date(replayTo) < new Date(replayFrom)) return toast.error("End date is before start date");

    setReplayLoading(true);
    setPlaying(false);
    try {
      const from = new Date(`${replayFrom}T00:00:00Z`).toISOString();
      const to = new Date(`${replayTo}T00:00:00Z`);
      to.setUTCDate(to.getUTCDate() + 1); // `to` is exclusive — include the whole end day
      const pings = await vehiclesApi.gpsHistoryRange(vehicleId, from, to.toISOString(), token);
      if (pings.length === 0) toast.error("No GPS pings in that range");
      setReplayHistory(pings);
      setCursorMs(pings.length > 0 ? new Date(pings[0].recorded_at).getTime() : 0);
    } catch {
      toast.error("Failed to load replay data");
      setReplayHistory([]);
    } finally {
      setReplayLoading(false);
    }
  };

  // rAF playback loop — advances cursorMs by (real elapsed ms * speed), so
  // the marker's speed through time stays accurate no matter the frame rate.
  useEffect(() => {
    if (!playing || !replayHistory || replayHistory.length < 2) return;
    lastTickRef.current = null;

    const tick = (now: number) => {
      if (lastTickRef.current == null) lastTickRef.current = now;
      const deltaReal = now - lastTickRef.current;
      lastTickRef.current = now;
      setCursorMs((c) => {
        const next = c + deltaReal * speed;
        if (next >= replayEndMs) {
          setPlaying(false);
          return replayEndMs;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, speed, replayHistory, replayEndMs]);

  const replayCursor = replayHistory ? interpolatePosition(replayHistory, cursorMs) : null;
  const vehicleTypeNameForReplay = types.find((t) => t.id === vehicle?.type_id)?.name;

  useEffect(() => {
    if (!vehicleId) return;
    let cancelled = false;

    const load = async (silent: boolean) => {
      try {
        const [v, h, t] = await Promise.all([
          vehiclesApi.get(vehicleId, token),
          vehiclesApi.gpsHistoryRange(vehicleId, shiftWindowStart().toISOString(), new Date().toISOString(), token).catch(() => []),
          silent ? Promise.resolve(types) : vehiclesApi.listTypes().catch(() => []),
        ]);
        if (cancelled) return;
        if (!silent) setTypes(t);
        if (silent && prevPingRef.current && v.last_ping_at && v.last_ping_at !== prevPingRef.current) {
          toast.success("Vehicle position updated");
        }
        prevPingRef.current = v.last_ping_at;
        setVehicle(v);
        setHistory(h);
      } catch {
        if (!silent) toast.error("Failed to load vehicle");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load(false);
    const interval = setInterval(() => load(true), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [vehicleId, token]);

  if (loading) {
    return <LoadingBlock label="Loading vehicle…" />;
  }
  if (!vehicle) {
    return <div className="p-6 text-sm text-muted-foreground">Vehicle not found.</div>;
  }

  const stopFlags = markStops(history);
  const pingsDesc = [...history].map((p, i) => ({ ...p, stopped: stopFlags[i] })).reverse();
  const pingTotalPages = Math.max(1, Math.ceil(pingsDesc.length / pingPageSize));
  const pingSafePage = Math.min(pingPage, pingTotalPages - 1);
  const pingsPaged = pingsDesc.slice(pingSafePage * pingPageSize, pingSafePage * pingPageSize + pingPageSize);
  const vehicleTypeName = types.find((t) => t.id === vehicle.type_id)?.name;
  const mapCurrent =
    vehicle.last_lat != null && vehicle.last_lng != null
      ? { lat: vehicle.last_lat, lng: vehicle.last_lng, label: vehicle.plate, vehicleType: vehicleTypeName }
      : null;

  return (
    <>
      <Topbar
        title={`Live tracking · ${vehicle.plate}`}
        subtitle={vehicle.device_id ? `Device ${vehicle.device_id}` : "No tracker linked"}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to={`/app/vehicles/${vehicle.id}`}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Link>
          </Button>
        }
      />
      <div className="flex-1 p-4 md:p-6">
        <Collapsible open={openSection === "live"} onOpenChange={(v) => v && setOpenSection("live")} className="surface-card overflow-hidden">
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 border-b p-4 text-left hover:bg-muted/50">
            <div>
              <h3 className="flex items-center gap-2 font-semibold"><MapPinned className="h-4 w-4" /> Live tracking</h3>
              <p className="text-xs text-muted-foreground">Current position, refreshed every {POLL_MS / 1000}s.</p>
            </div>
            <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${openSection === "live" ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
          <CollapsibleContent>
          <div className="border-b p-3">
            <LocationSearchBox
              token={token}
              enabled={searchEnabled}
              onSelect={(r) => mapRef.current?.goToSearchResult(r.lat, r.lng, r.label)}
            />
          </div>
          <LiveMap ref={mapRef} current={mapCurrent} history={history} className="aspect-[16/9] w-full md:aspect-[21/9]" />
          <div className="grid gap-3 border-t p-4 sm:grid-cols-3 md:grid-cols-5">
            <MetaBlock icon={Satellite} label="Device ID" value={vehicle.device_id ?? "Not provisioned"} />
            <MetaBlock icon={BatteryIcon} label="Battery" value={<BatteryBadge level={vehicle.last_battery_level} />} />
            <MetaBlock icon={SignalIcon} label="Signal" value={<SignalBadge level={vehicle.last_gsm_signal} />} />
            <MetaBlock
              icon={Gauge}
              label="Position"
              value={mapCurrent ? `${mapCurrent.lat.toFixed(5)}, ${mapCurrent.lng.toFixed(5)}` : "—"}
            />
            <MetaBlock
              icon={Clock}
              label="Last ping"
              value={vehicle.last_ping_at ? new Date(vehicle.last_ping_at).toLocaleString() : "—"}
            />
          </div>
          </CollapsibleContent>
        </Collapsible>

        <Collapsible open={openSection === "replay"} onOpenChange={(v) => v && setOpenSection("replay")} className="surface-card mt-4 overflow-hidden">
          <CollapsibleTrigger className="flex w-full flex-col gap-3 border-b p-4 text-left hover:bg-muted/50 md:flex-row md:items-end md:justify-between">
            <div>
              <h3 className="flex items-center gap-2 font-semibold"><History className="h-4 w-4" /> Replay</h3>
              <p className="text-xs text-muted-foreground">Pick a date range and play back this vehicle's movement.</p>
            </div>
            <ChevronDown className={`h-4 w-4 shrink-0 self-end text-muted-foreground transition-transform md:self-auto ${openSection === "replay" ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
          <CollapsibleContent>
          <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-end">
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={replayFrom} max={replayTo} onChange={(e) => setReplayFrom(e.target.value)} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={replayTo} min={replayFrom} max={todayStr()} onChange={(e) => setReplayTo(e.target.value)} className="h-9" />
              </div>
              <Button size="sm" onClick={runReplay} disabled={replayLoading}>
                {replayLoading ? "Loading…" : "Replay"}
              </Button>
            </div>
          </div>

          {replayHistory && replayHistory.length > 0 ? (
            <div className="p-4">
              <LocationSearchBox
                token={token}
                enabled={searchEnabled}
                onSelect={(r) => replayMapRef.current?.goToSearchResult(r.lat, r.lng, r.label)}
                className="mb-3"
              />
              <ReplayMap
                ref={replayMapRef}
                history={replayHistory}
                cursor={replayCursor}
                vehicleType={vehicleTypeNameForReplay}
                className="aspect-[16/9] w-full md:aspect-[21/9]"
              />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button variant="outline" size="icon" onClick={() => setCursorMs(replayStartMs)} aria-label="Restart">
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button size="icon" onClick={() => setPlaying((p) => !p)} aria-label={playing ? "Pause" : "Play"}>
                  {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                <div className="min-w-48 flex-1">
                  <Slider
                    min={replayStartMs}
                    max={replayEndMs}
                    step={1000}
                    value={[cursorMs]}
                    onValueChange={([v]) => {
                      setPlaying(false);
                      setCursorMs(v);
                    }}
                  />
                </div>
                <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                  {new Date(cursorMs).toLocaleString()}
                </span>
                <Select value={String(speed)} onValueChange={(v) => setSpeed(Number(v))}>
                  <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SPEED_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {replayHistory.length} ping{replayHistory.length === 1 ? "" : "s"} between{" "}
                {new Date(replayStartMs).toLocaleString()} and {new Date(replayEndMs).toLocaleString()}.
              </p>
            </div>
          ) : replayHistory && replayHistory.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No GPS pings in the selected range.</div>
          ) : null}
          </CollapsibleContent>
        </Collapsible>

        <div className="surface-card mt-4 overflow-hidden">
          <div className="flex items-center justify-between border-b p-4">
            <div>
              <h3 className="font-semibold">Pings received (today)</h3>
              <p className="text-xs text-muted-foreground">Every location report since 6am, latest first. Earlier days are in Replay.</p>
            </div>
            <span className="text-xs text-muted-foreground">{history.length} ping{history.length === 1 ? "" : "s"}</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Lat</TableHead>
                  <TableHead>Lng</TableHead>
                  <TableHead>Speed</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                      No pings received since 6am today.
                    </TableCell>
                  </TableRow>
                ) : (
                  pingsPaged
                    .map((p, i) => (
                      <TableRow key={`${p.recorded_at}-${i}`}>
                        <TableCell className="whitespace-nowrap text-xs">{new Date(p.recorded_at).toLocaleTimeString()}</TableCell>
                        <TableCell className="font-mono text-xs">{p.lat.toFixed(5)}</TableCell>
                        <TableCell className="font-mono text-xs">{p.lng.toFixed(5)}</TableCell>
                        <TableCell className="text-xs">{p.speed_kmh != null ? `${p.speed_kmh.toFixed(0)} km/h` : "—"}</TableCell>
                        <TableCell>
                          {p.stopped ? (
                            <Badge variant="outline" className="border-purple-400/30 bg-purple-400/10 text-purple-600 dark:text-purple-400">Stopped</Badge>
                          ) : (
                            <Badge variant="outline" className="border-success/30 bg-success/10 text-success">Moving</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </div>
          {pingsDesc.length > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                Rows per page
                <Select value={String(pingPageSize)} onValueChange={(v) => { setPingPageSize(Number(v)); setPingPage(0); }}>
                  <SelectTrigger className="h-8 w-[70px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {pingSafePage * pingPageSize + 1}–{Math.min((pingSafePage + 1) * pingPageSize, pingsDesc.length)} of {pingsDesc.length}
                </span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={pingSafePage === 0} onClick={() => setPingPage((p) => Math.max(0, p - 1))} aria-label="Previous page">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={pingSafePage >= pingTotalPages - 1} onClick={() => setPingPage((p) => Math.min(pingTotalPages - 1, p + 1))} aria-label="Next page">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function MetaBlock({ icon: Icon, label, value }: { icon: typeof Gauge; label: string; value: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}
